import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Users,
  Ticket,
  CheckSquare,
  Music,
  DollarSign,
  ListOrdered,
  Bot,
  Lock,
} from 'lucide-react'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Plan Richard (Phase 1 & 2 Deliverables)
   Password-protected. Same style as Master Plan, content from PHASE_1_DELIVERABLES.
   ═══════════════════════════════════════════════════════════════════════════ */

const MASTER_PLAN_PASSWORD = 'betadizee101'

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string }> = {
  sky: { border: 'border-sky-500/20', bg: 'bg-sky-500/[0.04]', text: 'text-sky-400', pill: 'bg-sky-500/15 text-sky-300' },
  violet: { border: 'border-violet-500/20', bg: 'bg-violet-500/[0.04]', text: 'text-violet-400', pill: 'bg-violet-500/15 text-violet-300' },
  emerald: { border: 'border-emerald-500/20', bg: 'bg-emerald-500/[0.04]', text: 'text-emerald-400', pill: 'bg-emerald-500/15 text-emerald-300' },
  amber: { border: 'border-amber-500/20', bg: 'bg-amber-500/[0.04]', text: 'text-amber-400', pill: 'bg-amber-500/15 text-amber-300' },
  cyan: { border: 'border-cyan-500/20', bg: 'bg-cyan-500/[0.04]', text: 'text-cyan-400', pill: 'bg-cyan-500/15 text-cyan-300' },
  rose: { border: 'border-rose-500/20', bg: 'bg-rose-500/[0.04]', text: 'text-rose-400', pill: 'bg-rose-500/15 text-rose-300' },
  zinc: { border: 'border-zinc-500/20', bg: 'bg-zinc-500/[0.04]', text: 'text-zinc-400', pill: 'bg-zinc-500/15 text-zinc-300' },
}

interface SectionContent {
  type: 'list'
  items: string[]
}
interface SectionContentPara {
  type: 'para'
  text: string
}
interface SectionContentTable {
  type: 'table'
  headers: string[]
  rows: string[][]
}

type SectionBlock = SectionContent | SectionContentPara | SectionContentTable

interface Section {
  id: string
  number: number
  icon: typeof Users
  title: string
  subtitle?: string
  color: string
  blocks: SectionBlock[]
}

const sections: Section[] = [
  {
    id: 'artist-promoter',
    number: 1,
    icon: Users,
    title: 'Do first: Artist account & Promoter preset account',
    color: 'violet',
    blocks: [
      {
        type: 'list',
        items: [
          'Add **Promoter** as a role in Invite Team Member (with Manager and Support).',
          'For Promoter role, show only **Shows** and **Team** in Section & Access Permissions; lock it so it can\'t be changed.',
          'For all invites: make **per-show filter** always on, at the top, and required (must pick shows).',
          'Remove **region filter** from the invite flow.',
          'Remove **Edit show info** option from the invite flow.',
          'When a promoter is in an artist account and has Team access, they can invite people — but only as **Manager** or **Support** (same Shows + Team scope).',
          'Promoter\'s team list only shows people **they** added, not the full account team.',
          'Allow **multiple promoters** per artist account.',
          'Add entry to team hub for promoters: **"Your dashboard" / "Promoter dashboard"** (or similar).',
          'Promoter dashboard: all their shows in one place, create shows (same Add Show flow), invite artists to shows.',
          'When promoter invites an artist to a show and artist has no Dizzy account: **create the artist account** and **add the promoter** to it automatically.',
          'When artist already has Dizzy: show request in **Team Management** (e.g. "Add [Promoter] as promoter to collaborate on these shows"); accept adds promoter to team and shows.',
          'Promoter can invite **Manager/Support** to their promoter account (same Shows + Team scope).',
          'Gate promoter dashboard with **access code** for now (e.g. "Dizzy 101"); payment later.',
        ],
      },
      { type: 'para', text: 'Full spec: see PROMOTER_ACCOUNT_DELIVERABLES.md.' },
    ],
  },
  {
    id: 'shows',
    number: 2,
    icon: Ticket,
    title: 'Shows',
    color: 'sky',
    blocks: [
      { type: 'para', text: 'This Week (priority)' },
      {
        type: 'list',
        items: [
          'Add **4 boxes on top of Shows**: adjust 3 for **At Risk**, **Sellout Status**, **Tickets to Sell**; keep **Next Up** as is.',
          '**Click to view shows** — user can click a show to view it (no delete). Put delete on the 3-dot menu for now; bulk delete later.',
          '**One-click Sold Out** — add to 3-dot menu on a show.',
          'Build **Money section** (see Money list below).',
        ],
      },
      { type: 'para', text: 'Next' },
      {
        type: 'list',
        items: [
          '**Ticket Tracking Uploader** — get it working; support different file types and plain text.',
          '**Booking Google Sheet** — integration/functionality.',
        ],
      },
    ],
  },
  {
    id: 'tasks',
    number: 3,
    icon: CheckSquare,
    title: 'Tasks',
    color: 'emerald',
    blocks: [
      { type: 'para', text: 'This Week (priority)' },
      {
        type: 'list',
        items: [
          'Add **4 boxes on top of Tasks**: **This Week**, **Overdue**, **Approvals**, **Waiting On**.',
          '**Filters in Tasks**: My Tasks vs Team Tasks, For My Approval, Sent for Approval (desktop; already on mobile).',
          '**Approvals in Tasks**: if a task is assigned, allow approval or denial. Show **Approve / Deny** on the user\'s side in the Task sheet; add an "approvals" column.',
          '**Click to view task** — click a task to open and see details (no need to go 3 dots → edit).',
          '**Mark as complete** for tasks.',
          '**Assign a task to more than one person**.',
          '**Comment / note on a task**.',
        ],
      },
    ],
  },
  {
    id: 'music',
    number: 4,
    icon: Music,
    title: 'Music',
    color: 'cyan',
    blocks: [
      { type: 'para', text: 'Next' },
      {
        type: 'list',
        items: [
          '**Streaming** — develop streaming functionality.',
          '**Writer Credits** — implement writer credits.',
        ],
      },
    ],
  },
  {
    id: 'money',
    number: 5,
    icon: DollarSign,
    title: 'Money',
    color: 'amber',
    blocks: [
      { type: 'para', text: 'This Week (priority)' },
      {
        type: 'list',
        items: [
          'Build the **Money section** — see "Money" tab in the Google Sheet for full breakdown.',
        ],
      },
    ],
  },
  {
    id: 'order',
    number: 6,
    icon: ListOrdered,
    title: 'Order at a glance',
    color: 'rose',
    blocks: [
      {
        type: 'table',
        headers: ['Order', 'Area', 'When'],
        rows: [
          ['1', 'Artist account & Promoter preset account', 'First'],
          ['2', 'Shows (4 boxes, click to view, one-click sold out, Money section)', 'This Week'],
          ['3', 'Tasks (4 boxes, filters, approvals, click to view, complete, multi-assign, comments)', 'This Week'],
          ['4', 'Money section', 'This Week'],
          ['5', 'Shows (ticket uploader, booking Google Sheet); Music (streaming, writer credits)', 'Next'],
        ],
      },
    ],
  },
  {
    id: 'phase2',
    number: 7,
    icon: Bot,
    title: 'Phase 2 — AI Intelligence',
    subtitle: 'After Phase 1. Scope TBD — add specific deliverables as they\'re defined.',
    color: 'zinc',
    blocks: [
      {
        type: 'list',
        items: [
          'Define AI intelligence roadmap (what we want AI to do across Shows, Tasks, Music, Team, etc.).',
          'Prioritize first AI use cases (e.g. insights, suggestions, automation, chat).',
          'Implement and ship first AI intelligence features.',
          'Iterate based on usage and feedback.',
        ],
      },
    ],
  },
]

function renderInlineBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  )
}

export default function PlanRichardPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === MASTER_PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">Plan Richard</span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view Phase 1 & 2 deliverables.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View Plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              Plan Richard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-12">
        <div className="mb-2">
          <h1 className="text-xl font-semibold text-white">Phase 1 & Phase 2 — Simple Deliverables</h1>
          <p className="text-[13px] text-zinc-500 mt-1 italic">
            Phase 1: Dizee Beta – Final Dev Week. Do in this order. Bolded / “This Week” items matter most. Phase 2: AI Intelligence (after Phase 1).
          </p>
        </div>

        <nav className="flex flex-wrap gap-2">
          {sections.map((s) => {
            const c = colorMap[s.color] || colorMap.sky
            return (
              <button
                key={s.id}
                onClick={() => {
                  if (!openSections.has(s.id)) toggleSection(s.id)
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all hover:scale-[1.03] ${c.border} ${c.bg} ${c.text}`}
              >
                {s.number}. {s.title.replace(/^Do first: /, '').replace(/ —.*$/, '')}
              </button>
            )
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand All
          </button>
          <span className="text-zinc-700">|</span>
          <button
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse All
          </button>
        </div>

        <div className="space-y-4">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white truncate">{section.title}</h2>
                    </div>
                    {section.subtitle && (
                      <p className="text-[12px] text-zinc-500 mt-1 truncate">{section.subtitle}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 space-y-5">
                    {section.blocks.map((block, bi) => {
                      if (block.type === 'para') {
                        return (
                          <p key={bi} className="text-[13px] text-zinc-400 font-medium">
                            {renderInlineBold(block.text)}
                          </p>
                        )
                      }
                      if (block.type === 'list') {
                        return (
                          <ul key={bi} className="space-y-2 list-none">
                            {block.items.map((item, i) => (
                              <li key={i} className="flex items-start gap-2.5">
                                <span className="text-zinc-500 mt-1.5 flex-shrink-0">•</span>
                                <span className="text-[13px] text-zinc-400 leading-relaxed">
                                  {renderInlineBold(item)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )
                      }
                      if (block.type === 'table') {
                        return (
                          <div key={bi} className="overflow-x-auto rounded-lg border border-white/[0.06]">
                            <table className="w-full text-left text-[12px]">
                              <thead>
                                <tr className="border-b border-white/[0.06]">
                                  {block.headers.map((h, i) => (
                                    <th key={i} className="px-4 py-3 text-zinc-500 font-semibold uppercase tracking-wider">
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {block.rows.map((row, ri) => (
                                  <tr key={ri} className="border-b border-white/[0.04] last:border-0">
                                    {row.map((cell, ci) => (
                                      <td key={ci} className="px-4 py-3 text-zinc-400">
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <footer className="pt-8 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700 italic">Source: Dizee Beta – Final Dev Week for Phase 1 (sheet).</span>
        </footer>
      </main>
    </div>
  )
}
